import { useClerk } from '@clerk/react-router';
import { captureException } from '@sentry/react';
import { useState, type ReactElement, type ReactNode } from 'react';
import { cacheExchange, createClient, errorExchange, fetchExchange, getOperationName, Provider } from 'urql';

export type UrqlProviderProps = {
    children: ReactNode;
};

export function UrqlProvider({ children }: UrqlProviderProps): ReactElement {
    const clerk = useClerk();

    const [client] = useState(() =>
        createClient({
            url: '/api/graphql',
            exchanges: [
                cacheExchange,
                errorExchange({
                    onError: (error, operation) => {
                        // AuthGuard is already handling the redirect on the
                        // Clerk state change; the 401 is expected noise on the
                        // way there.
                        if (error.response instanceof Response && error.response.status === 401) {
                            return;
                        }
                        captureException(error, {
                            contexts: {
                                graphql: {
                                    operationName: getOperationName(operation.query),
                                    kind: operation.kind,
                                    variables: operation.variables,
                                },
                            },
                        });
                    },
                }),
                fetchExchange,
            ],
            fetch: (input, init) => {
                if (clerk.session) {
                    // add the rate limit key
                    const headers = new Headers(init?.headers);
                    headers.set('x-clerk-user-id', clerk.session.user.id);
                    return fetch(input, { ...init, headers });
                }
                return fetch(input, init);
            },
        }),
    );

    return <Provider value={client}>{children}</Provider>;
}
